import { DownloadIcon } from '@approvalmax/ui';
import {
    Box,
    Button,
    DatePicker,
    Form,
    Grid,
    Label,
    Popup,
    Select,
    Text,
    ToggleButton,
} from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { memo, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { getCountryFlagByCountryCode } from 'shared/helpers';
import { useWalletAvailableCurrencies } from 'shared/hooks/(wallets)';
import { z } from 'zod';

import { getCountryCodeByCurrency } from '../../Wallets.helpers';
import { CurrencyMenuItem } from '../CurrencyMenuItem/CurrencyMenuItem';
import { fileFormats } from './BankStatementReportPopupContent.constants';
import { filterAvailableCurrencies } from './BankStatementReportPopupContent.helpers';
import { messages } from './BankStatementReportPopupContent.messages';
import { schema } from './BankStatementReportPopupContent.schema';
import { FileFormat, ReportPeriod } from './BankStatementReportPopupContent.types';

export const BankStatementReportPopupContent = memo(() => {
    const form = useForm<z.infer<typeof schema>>({
        defaultValues: {
            period: ReportPeriod.LastWeek,
            format: FileFormat.PDF,
        },
        resolver: zodResolver(schema),
    });

    const { currencies, isLoading } = useWalletAvailableCurrencies({
        needBalance: false,
        walletFilter: filterAvailableCurrencies,
    });

    const handleSubmit = form.handleSubmit(async (values) => {
        console.info(values);
    });

    const [period, currency] = form.watch(['period', 'currency']);

    const IconFlag = useMemo(() => {
        return currency ? getCountryFlagByCountryCode(getCountryCodeByCurrency(currency)) : undefined;
    }, [currency]);

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button
                        color='blue80'
                        size='medium'
                        type='submit'
                        startIcon={<DownloadIcon size={16} />}
                        progress={form.formState.isSubmitting}
                    >
                        {messages.download}
                    </Button>
                }
            />

            <Popup.Body>
                <Grid rowGap={24}>
                    <Text font='body' fontSize='medium'>
                        {messages.description}
                    </Text>

                    <ToggleButton.Group block name='period'>
                        <ToggleButton value={ReportPeriod.LastWeek}>{messages.lastWeek}</ToggleButton>

                        <ToggleButton value={ReportPeriod.LastMonth}>{messages.lastMonth}</ToggleButton>

                        <ToggleButton value={ReportPeriod.LastYear}>{messages.lastYear}</ToggleButton>

                        <ToggleButton value={ReportPeriod.Custom}>{messages.custom}</ToggleButton>
                    </ToggleButton.Group>

                    <Grid rowGap={16}>
                        {period === ReportPeriod.Custom && (
                            <div>
                                <Label>{messages.reportDateRange}</Label>

                                <Grid
                                    gridTemplateColumns='max-content 1fr max-content 1fr'
                                    columnGap={4}
                                    alignItems='center'
                                >
                                    <Text color='midnight70' font='label' fontSize='small'>
                                        {messages.from}
                                    </Text>

                                    <DatePicker name='dateFrom' size='medium' placeholder={messages.datePlaceholder} />

                                    <Text color='midnight70' font='label' fontSize='small'>
                                        {messages.to}
                                    </Text>

                                    <DatePicker name='dateTo' size='medium' placeholder={messages.datePlaceholder} />
                                </Grid>
                            </div>
                        )}

                        <Select
                            name='currency'
                            label={messages.currency}
                            placeholder={messages.currencyPlaceholder}
                            items={currencies ?? []}
                            progress={isLoading}
                            customMenuItem={(item) => <CurrencyMenuItem {...item} subName={item.currencyName} />}
                            autocomplete
                            clearable={false}
                            itemNameKey='id'
                            startIcon={IconFlag ? <IconFlag width={22} /> : undefined}
                        />

                        <Box width={120}>
                            <Select
                                name='format'
                                label={messages.fileFormat}
                                placeholder={messages.fileFormatPlaceholder}
                                items={fileFormats}
                                size='medium'
                                clearable={false}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Popup.Body>
        </Form>
    );
});

BankStatementReportPopupContent.displayName = 'BankStatementReportPopupContent';
